import { useMutation, useQuery } from '@tanstack/react-query';
import { getSecurityDetails } from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { createContext, useContext, useMemo, useState } from 'react';
import { getSecurityEvents } from '@dealstack/frontend/app/services/securityEvent.service.ts';
import { TableSorting } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import {
	PaginationDto,
	PermissionRole,
	SecurityEventFilterDto,
} from '@dealstack/shared/lib/generated';
import { useParams, useSearchParams } from 'react-router-dom';
import {
	getDealLoanDefinitions,
	getSecurityAllocations,
} from '@dealstack/frontend/app/services/securityAllocations.service.ts';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { ResourceType } from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { formatDateToBEIgnoreTimezone } from '@dealstack/shared/lib/format/dates/date.ts';

interface GetSecurityEventParams {
	securityId: string;
	pagination?: PaginationDto;
	filter?: SecurityEventFilterDto;
	sortBy?: TableSorting[];
}

export const SecurityDetailsControllerContext = createContext<
	ReturnType<typeof useSecurityDetailsController>
>({} as ReturnType<typeof useSecurityDetailsController>);

export const useSecurityDetailsController = () => {
	const { securityId } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const [securityEventListParams, setSecurityEventListParams] =
		useState<GetSecurityEventParams | null>({
			securityId: securityId ?? '',
			pagination: {
				skip: 0,
				take: 50,
			},
		});

	const filterDate = useMemo(() => {
		const filterDateParam = searchParams.get('filterDate');

		if (filterDateParam) {
			return new Date(filterDateParam);
		}

		return null;
	}, [searchParams]);

	const { dealId } = useContext(DealContext) || {};

	const { hasPermission: hasEditorPermission } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: defaultToEmptyString(dealId),
		permissionRole: PermissionRole.EDITOR,
	});

	const getSecurityEventRequest = useQuery({
		queryKey: ['securityEvents', securityEventListParams],
		queryFn: () => getSecurityEvents(securityEventListParams),
		enabled: !!securityId,
	});
	const getSecurityDetailsRequest = useMutation({
		mutationFn: ({ securityId, date }: { securityId: string; date?: string }) =>
			getSecurityDetails(securityId, date),
	});
	const getAllocationsRequest = useQuery({
		queryKey: ['', { securityId: securityId ?? '' }],
		queryFn: () => getSecurityAllocations({ securityId: securityId ?? '' }),
		enabled: !!securityId,
	});

	const getDealLoanDefinitionsRequest = useQuery({
		queryKey: [API_PATHS.SECURITY_LOAN_DEFINITIONS_GET],
		queryFn: () => getDealLoanDefinitions(),
	});

	const refetchSecurityDetails = (securityId: string) => {
		const dateParam = filterDate ? formatDateToBEIgnoreTimezone(filterDate) : undefined;
		getSecurityDetailsRequest.mutate({ securityId, date: dateParam });
	};

	const setFilterDate = (date: Date | null) => {
		if (date) {
			const formattedDate = formatDateToBEIgnoreTimezone(date);

			setSearchParams({
				filterDate: formattedDate,
			});
		}
	};

	const loanDefinitionsOptions = useMemo(() => {
		return getDealLoanDefinitionsRequest.data?.map((loanDefinition) => ({
			label: `${loanDefinition.mipName}: ${loanDefinition.loanDefinitionName}`,
			value: loanDefinition.loanDefinitionId,
		}));
	}, [getDealLoanDefinitionsRequest.data]);

	return {
		getSecurityDetailsRequest,
		refetchSecurityDetails,
		securityEventListParams,
		setSecurityEventListParams,
		getSecurityEventRequest,
		getAllocationsRequest,
		securityEvents: getSecurityEventRequest.data?.securityEvents,
		hasEditorPermission,
		setFilterDate,
		filterDate,
		loanDefinitionsOptions,
		refetchLoanDefinitions: getDealLoanDefinitionsRequest.refetch,
	};
};
